// ---------------------------------------------------------------------------
// Bell Operations Console — scoped styles
// ---------------------------------------------------------------------------
// The console's brand CSS is ported verbatim from the original standalone page
// but every selector is scoped under `.dgc` so its global element rules
// (button/select/.card/.chip/.btn …) can never leak into the Chakra dashboard.
// Keyframes are prefixed (dgc*) for the same reason. The top rail is switched
// from position:sticky to relative because the console is embedded inside the
// admin panel rather than owning the whole viewport.
// ---------------------------------------------------------------------------
import React, {useEffect} from "react";

const FONTS_HREF =
    "https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Mukta:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap";

export const CONSOLE_CSS = `
.dgc{
  --navy:#09152B; --navy-2:#132340; --navy-3:#1D3055;
  --blue:#0B66DB; --blue-soft:#E8F1FE;
  --emerald:#047E4F; --emerald-soft:#E9F5EF;
  --gold:#FFB305; --gold-soft:#FFF6E2;
  --fail:#C0271A; --fail-soft:#FCEDEB;
  --offline:#6C7A91; --offline-soft:#EFF2F6;
  --paper:#F3F5F9; --card:#FFFFFF;
  --ink:#0B1526; --ink-2:#33425C; --muted:#6B7A93;
  --line:#E3E8F0; --line-2:#EDF1F7;
  --display:'Baloo 2','Mukta',system-ui,sans-serif;
  --body:'Mukta',system-ui,-apple-system,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,'SF Mono',monospace;
  --r-s:6px; --r-m:10px; --r-l:16px;
  --shadow:0 1px 2px rgba(11,21,38,.05),0 8px 24px -12px rgba(11,21,38,.18);
  --shadow-lg:0 24px 60px -20px rgba(11,21,38,.4);
  --rail-h:64px;
  background:var(--paper);color:var(--ink);font-family:var(--body);
  font-size:15px;line-height:1.45;-webkit-font-smoothing:antialiased;
  position:relative;border-radius:16px;
}
/* Clear the floating admin navbar (absolute, ~75px tall near the viewport top)
   so the top rail and its school picker aren't hidden under it. Matches the
   overall view's offset so switching between the two doesn't jump. */
.dgc{margin-top:70px}
@media (max-width:767px){.dgc{margin-top:120px}}
.dgc *{box-sizing:border-box}
.dgc button,.dgc select,.dgc input{font-family:inherit;font-size:inherit;color:inherit}
.dgc button{cursor:pointer;border:0;background:none}
.dgc [hidden]{display:none !important}
.dgc .mono{font-family:var(--mono);font-variant-numeric:tabular-nums}
.dgc .sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.dgc :focus-visible{outline:2.5px solid var(--blue);outline-offset:2px;border-radius:4px}

.dgc .rail{
  position:relative;z-index:40;min-height:var(--rail-h);
  display:flex;align-items:center;gap:18px;padding:0 20px;
  background:linear-gradient(100deg,var(--navy) 0%,var(--navy-2) 60%,var(--navy-3) 100%);
  box-shadow:0 1px 0 rgba(255,255,255,.06),0 6px 20px -8px rgba(9,21,43,.55);
  border-radius:16px 16px 0 0;
}
.dgc .brand{display:flex;align-items:center;gap:10px;flex:none}
.dgc .brand img{height:36px;width:auto;display:block}
.dgc .brand .bt{font-family:var(--display);font-weight:700;color:#fff;font-size:19px;line-height:1;letter-spacing:.1px}
.dgc .brand .bs{display:block;font-family:var(--body);font-weight:500;font-size:11px;letter-spacing:.14em;
  text-transform:uppercase;color:#7FA6DE;margin-top:3px}
.dgc .rail-sep{width:1px;height:30px;background:rgba(255,255,255,.14);flex:none}
.dgc .rail-tools{display:flex;align-items:center;gap:10px;margin-left:auto;flex-wrap:nowrap}

.dgc .field{position:relative;display:flex;align-items:center}
.dgc .field>svg{position:absolute;right:10px;pointer-events:none;opacity:.7}
.dgc .picker{
  appearance:none;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.16);
  color:#fff;border-radius:var(--r-m);padding:7px 32px 7px 8px;font-weight:600;font-size:14px;
  min-width:230px;max-width:320px;transition:border-color .15s,background .15s;
  display:flex;align-items:center;gap:9px;width:100%;text-align:left;cursor:pointer;
}
.dgc .picker:hover{background:rgba(255,255,255,.14);border-color:rgba(255,255,255,.3)}
.dgc .picker:disabled{opacity:.6;cursor:default}
.dgc .picker .slabel{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

/* Square school logo tile used in the button and the dropdown options. */
.dgc .slogo{width:26px;height:26px;flex:0 0 26px;border-radius:6px;object-fit:cover;
  background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.18)}
.dgc .slogo--ph{display:flex;align-items:center;justify-content:center;
  font-weight:800;font-size:13px;color:#fff}

/* Custom school picker dropdown menu. */
.dgc .schoolpick{position:relative}
.dgc .picker-menu{position:absolute;top:calc(100% + 6px);left:0;z-index:40;
  min-width:100%;max-width:360px;background:#fff;border:1px solid rgba(0,0,0,.1);
  border-radius:var(--r-m);box-shadow:0 12px 32px rgba(16,32,64,.22);overflow:hidden}
.dgc .picker-search{display:flex;align-items:center;gap:8px;padding:9px 10px;
  border-bottom:1px solid rgba(0,0,0,.08);color:#7A879B}
.dgc .picker-search input{flex:1;border:0;outline:0;background:transparent;
  color:var(--ink);font-size:14px;font-weight:600}
.dgc .picker-search input::placeholder{color:#9AA6B8;font-weight:500}
.dgc .picker-list{list-style:none;margin:0;padding:6px;max-height:300px;overflow-y:auto}
.dgc .picker-opt{display:flex;align-items:center;gap:9px;padding:7px 8px;border-radius:8px;
  cursor:pointer;color:var(--ink);font-weight:600;font-size:14px}
.dgc .picker-opt .slogo{background:#EEF2F8;border-color:rgba(0,0,0,.08)}
.dgc .picker-opt .slogo--ph{color:var(--navy)}
.dgc .picker-opt .slabel{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dgc .picker-opt:hover{background:#F1F5FB}
.dgc .picker-opt.on{background:var(--gold-soft)}
.dgc .picker-empty{padding:14px 10px;text-align:center;color:#8A94A6;font-size:13.5px;font-weight:500}

.dgc .weeknav{display:flex;align-items:center;background:rgba(255,255,255,.08);
  border:1px solid rgba(255,255,255,.16);border-radius:var(--r-m);overflow:hidden}
.dgc .weeknav button{padding:8px 11px;color:#CFE0F6;display:flex;align-items:center;transition:background .15s}
.dgc .weeknav button:hover{background:rgba(255,255,255,.14);color:#fff}
.dgc .weeknav button:disabled{opacity:.3;cursor:not-allowed}
.dgc .weeknav .wlabel{padding:0 12px;color:#fff;font-weight:600;font-size:13.5px;white-space:nowrap;
  border-left:1px solid rgba(255,255,255,.14);border-right:1px solid rgba(255,255,255,.14);
  height:36px;display:flex;align-items:center;font-family:var(--mono);letter-spacing:-.2px}
.dgc .ghost{color:#CFE0F6;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.08);
  border-radius:var(--r-m);padding:8px 12px;font-weight:600;font-size:13.5px;display:flex;align-items:center;gap:7px;
  transition:background .15s,color .15s}
.dgc .ghost:hover{background:rgba(255,255,255,.16);color:#fff}
.dgc .ghost.on{background:var(--gold);border-color:var(--gold);color:var(--navy)}
.dgc .lang{font-weight:700;min-width:52px;justify-content:center}

.dgc .wrap{max-width:1560px;margin:0 auto;padding:20px 20px 64px}
.dgc .banner{display:flex;align-items:center;gap:10px;background:var(--gold-soft);border:1px solid #F3DCA6;
  color:#6B4A00;border-radius:var(--r-m);padding:10px 14px;font-size:13.5px;margin-bottom:16px;font-weight:500}
.dgc .banner b{font-weight:700}
.dgc .banner code{font-family:var(--mono);font-size:12.5px;background:rgba(0,0,0,.06);padding:1px 5px;border-radius:4px}

.dgc .context{display:flex;align-items:center;gap:22px;flex-wrap:wrap;
  background:var(--card);border:1px solid var(--line);border-radius:var(--r-l);
  padding:16px 20px;box-shadow:var(--shadow);margin-bottom:14px}
.dgc .ctx-name{font-family:var(--display);font-weight:700;font-size:22px;line-height:1.15;letter-spacing:.1px}
.dgc .ctx-sub{color:var(--muted);font-size:13px;display:flex;gap:8px;align-items:center;margin-top:2px}
.dgc .chip{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;
  padding:4px 9px;border-radius:99px;background:var(--blue-soft);color:#0A4FA8;white-space:nowrap}
.dgc .chip.mut{background:var(--offline-soft);color:var(--ink-2)}
.dgc .ctx-meta{margin-left:auto;display:flex;gap:26px;flex-wrap:wrap}
.dgc .meta-k{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);font-weight:600}
.dgc .meta-v{font-family:var(--mono);font-size:14px;font-weight:500;margin-top:2px;white-space:nowrap;display:flex;align-items:center;gap:6px}
.dgc .dot{width:8px;height:8px;border-radius:99px;display:inline-block;flex:none}
.dgc .dot.ok{background:var(--emerald);box-shadow:0 0 0 3px var(--emerald-soft)}
.dgc .dot.bad{background:var(--fail);box-shadow:0 0 0 3px var(--fail-soft)}
.dgc .pulse{animation:dgcPulse 2.4s ease-out infinite}
@keyframes dgcPulse{0%{box-shadow:0 0 0 0 rgba(4,126,79,.5)}70%{box-shadow:0 0 0 9px rgba(4,126,79,0)}100%{box-shadow:0 0 0 0 rgba(4,126,79,0)}}

.dgc .kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:14px}
.dgc .kpi{background:var(--card);border:1px solid var(--line);border-radius:var(--r-l);padding:14px 16px 13px;
  box-shadow:var(--shadow);position:relative;overflow:hidden}
.dgc .kpi::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--line)}
.dgc .kpi.k-played::before{background:var(--emerald)} .dgc .kpi.k-fail::before{background:var(--fail)}
.dgc .kpi.k-off::before{background:var(--offline)} .dgc .kpi.k-sched::before{background:var(--blue)}
.dgc .kpi.k-rate::before{background:var(--gold)}
.dgc .kpi .kl{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);font-weight:600}
.dgc .kpi .kv{font-family:var(--display);font-weight:700;font-size:30px;line-height:1.1;margin-top:4px;letter-spacing:-.5px}
.dgc .kpi .kd{font-size:12.5px;color:var(--muted);margin-top:1px}
.dgc .kpi.k-fail .kv{color:var(--fail)} .dgc .kpi.k-played .kv{color:var(--emerald)}
.dgc .kpi.clickable{cursor:pointer;transition:transform .12s,box-shadow .12s}
.dgc .kpi.clickable:hover{transform:translateY(-2px);box-shadow:0 10px 26px -14px rgba(11,21,38,.45)}
.dgc .spark{display:flex;gap:2px;align-items:flex-end;height:22px;margin-top:8px}
.dgc .spark i{flex:1;background:var(--line);border-radius:2px 2px 0 0;min-height:2px}

.dgc .card{background:var(--card);border:1px solid var(--line);border-radius:var(--r-l);box-shadow:var(--shadow);overflow:hidden}
.dgc .card-head{display:flex;align-items:center;gap:14px;padding:14px 18px;border-bottom:1px solid var(--line-2);flex-wrap:wrap}
.dgc .card-title{font-family:var(--display);font-weight:700;font-size:17px}
.dgc .card-title span{color:var(--muted);font-family:var(--body);font-weight:500;font-size:13px;margin-left:8px}
.dgc .filters{margin-left:auto;display:flex;gap:6px;align-items:center;flex-wrap:wrap}
.dgc .fchip{border:1px solid var(--line);background:#fff;border-radius:99px;padding:5px 11px;font-size:12.5px;
  font-weight:600;color:var(--ink-2);display:flex;align-items:center;gap:6px;transition:.15s}
.dgc .fchip:hover{border-color:var(--ink-2)}
.dgc .fchip[aria-pressed="true"]{background:var(--navy);border-color:var(--navy);color:#fff}
.dgc .fchip[aria-pressed="true"] .sw{box-shadow:0 0 0 2px rgba(255,255,255,.35)}
.dgc .sw{width:9px;height:9px;border-radius:3px;display:inline-block}

.dgc .gridscroll{overflow-x:auto;overscroll-behavior-x:contain}
.dgc .bellgrid{display:grid;grid-template-columns:190px repeat(7,minmax(122px,1fr));min-width:900px}
.dgc .gh{position:sticky;top:0;z-index:5;background:#fff;border-bottom:1px solid var(--line);padding:11px 12px 9px}
.dgc .gh.corner{position:sticky;left:0;z-index:6;border-right:1px solid var(--line);
  display:flex;flex-direction:column;justify-content:flex-end}
.dgc .gh .dw{font-family:var(--display);font-weight:700;font-size:15px;line-height:1.1}
.dgc .gh .dd{font-family:var(--mono);font-size:11.5px;color:var(--muted);margin-top:2px;letter-spacing:-.2px}
.dgc .gh.today{background:linear-gradient(180deg,var(--blue-soft),#fff)}
.dgc .gh.today .dw{color:#0A4FA8}
.dgc .gh.holiday .dw{color:#8A6400}
.dgc .daybar{display:flex;gap:2px;height:4px;margin-top:9px;border-radius:99px;overflow:hidden;background:var(--line-2)}
.dgc .daybar i{display:block;height:100%}
.dgc .hpill{display:inline-block;font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
  background:var(--gold-soft);color:#7A5600;padding:2px 7px;border-radius:99px;margin-top:6px}
.dgc .corner .ck{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);font-weight:600}

.dgc .rowlabel{position:sticky;left:0;z-index:3;background:#fff;border-right:1px solid var(--line);
  border-bottom:1px solid var(--line-2);padding:10px 12px;display:flex;flex-direction:column;justify-content:center;gap:2px}
.dgc .rowlabel .pn{font-weight:600;font-size:14px;line-height:1.2}
.dgc .rowlabel .pt{font-family:var(--mono);font-size:12px;color:var(--muted);display:flex;align-items:center;gap:7px}
.dgc .rowlabel .strikes{font-size:11px;color:var(--blue);font-weight:600}
.dgc .rowlabel.break{background:#FBFCFE}
.dgc .rowlabel.break .pn{color:var(--ink-2)}

.dgc .cell{
  position:relative;display:flex;flex-direction:column;justify-content:center;gap:7px;
  border-bottom:1px solid var(--line-2);border-right:1px solid var(--line-2);
  padding:10px 12px;min-height:66px;text-align:left;width:100%;
  background:#fff;transition:background .14s,transform .14s;
  --mark:var(--line);--markfill:var(--line);
}
.dgc .cell:hover{background:#FAFBFD;z-index:2}
.dgc .cell.dim{opacity:.24;filter:saturate(.3)}
.dgc .cell::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:transparent}
.dgc .tally{display:flex;align-items:flex-end;gap:3px;height:17px}
.dgc .tally i{width:3.5px;height:14px;border-radius:2px;background:var(--markfill);border:1.5px solid var(--mark);
  box-sizing:border-box;transform-origin:bottom}
.dgc .tally i:nth-child(4n){transform:rotate(0)}
.dgc .tally .more{width:auto;height:auto;border:0;background:none;font-family:var(--mono);font-size:11px;
  color:var(--muted);align-self:center;line-height:1}
.dgc .ctime{font-family:var(--mono);font-size:12.5px;font-weight:500;letter-spacing:-.3px;display:flex;align-items:center;gap:5px}
.dgc .cnote{font-size:11px;color:var(--muted);line-height:1.2}

.dgc .cell.s-played{background:var(--emerald-soft);--mark:var(--emerald);--markfill:var(--emerald)}
.dgc .cell.s-played::before{background:var(--emerald)}
.dgc .cell.s-played .ctime{color:#04603C}
.dgc .cell.s-played:hover{background:#DFF0E8}

.dgc .cell.s-late{background:var(--gold-soft);--mark:#C98A00;--markfill:var(--gold)}
.dgc .cell.s-late::before{background:var(--gold)}
.dgc .cell.s-late .ctime{color:#7A5600}
.dgc .cell.s-late:hover{background:#FDEFD2}

.dgc .cell.s-failed{background:var(--fail-soft);--mark:var(--fail);--markfill:transparent}
.dgc .cell.s-failed::before{background:var(--fail)}
.dgc .cell.s-failed .ctime{color:var(--fail);font-weight:600}
.dgc .cell.s-failed:hover{background:#FAE1DD}

.dgc .cell.s-offline{--mark:var(--offline);--markfill:transparent;
  background:repeating-linear-gradient(135deg,#F5F7FA 0 6px,#EAEEF4 6px 12px)}
.dgc .cell.s-offline::before{background:var(--offline)}
.dgc .cell.s-offline .ctime{color:var(--offline)}

.dgc .cell.s-pending{background:#fff;--mark:#C9D3E2;--markfill:transparent}
.dgc .cell.s-pending .ctime{color:#93A0B5}
.dgc .cell.s-pending .tally i{border-style:dashed}

.dgc .cell.s-holiday{background:#FBFAF6;--mark:#E6DFCB;--markfill:transparent;cursor:default}
.dgc .cell.s-holiday .ctime{color:#B0A88F}

.dgc .cell.now{box-shadow:inset 0 0 0 2px var(--blue);z-index:3}
.dgc .cell .livedot{position:absolute;top:8px;right:8px;width:7px;height:7px;border-radius:99px;background:var(--blue)}
.dgc .cell .warn{position:absolute;top:7px;right:8px;color:var(--fail);line-height:0}
@keyframes dgcRing{0%{box-shadow:inset 0 0 0 2px var(--blue),0 0 0 0 rgba(11,102,219,.35)}
  70%{box-shadow:inset 0 0 0 2px var(--blue),0 0 0 10px rgba(11,102,219,0)}
  100%{box-shadow:inset 0 0 0 2px var(--blue),0 0 0 0 rgba(11,102,219,0)}}
.dgc .cell.now{animation:dgcRing 2.6s ease-out infinite}
.dgc .cell.rise{animation:dgcRise .4s cubic-bezier(.2,.8,.3,1) backwards}
@keyframes dgcRise{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}

.dgc .legend{display:flex;gap:20px;flex-wrap:wrap;align-items:center;padding:13px 18px;border-top:1px solid var(--line-2);
  background:#FBFCFE;font-size:12.5px;color:var(--ink-2)}
.dgc .lg{display:flex;align-items:center;gap:8px}
.dgc .lg .box{width:26px;height:20px;border-radius:5px;border:1px solid var(--line);display:flex;align-items:center;
  justify-content:center;gap:2px}
.dgc .lg .box i{width:3px;height:9px;border-radius:2px}
.dgc .legend .hint{margin-left:auto;color:var(--muted)}

.dgc .scrim{position:fixed;inset:0;background:rgba(9,21,43,.42);backdrop-filter:blur(2px);z-index:1400;opacity:0;
  transition:opacity .22s}
.dgc .scrim.open{opacity:1}
.dgc .drawer{position:fixed;top:0;right:0;bottom:0;width:min(430px,100%);background:#fff;z-index:1401;
  box-shadow:var(--shadow-lg);transform:translateX(100%);transition:transform .28s cubic-bezier(.3,.9,.3,1);
  display:flex;flex-direction:column}
.dgc .drawer.open{transform:none}
.dgc .dhead{padding:18px 20px;color:#fff;background:linear-gradient(120deg,var(--navy),var(--navy-3));position:relative}
.dgc .dhead .close{position:absolute;top:14px;right:14px;color:#B9CBE6;padding:6px;border-radius:8px}
.dgc .dhead .close:hover{background:rgba(255,255,255,.12);color:#fff}
.dgc .dhead .eyebrow{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#7FA6DE;font-weight:600}
.dgc .dhead h2{font-family:var(--display);font-size:23px;font-weight:700;margin:5px 0 0}
.dgc .dhead .when{font-family:var(--mono);font-size:13px;color:#C6D7EE;margin-top:5px}
.dgc .dbadge{display:inline-flex;align-items:center;gap:7px;margin-top:12px;padding:6px 12px;border-radius:99px;
  font-weight:700;font-size:13px}
.dgc .dbody{padding:18px 20px;overflow-y:auto;flex:1}
.dgc .drow{display:flex;justify-content:space-between;gap:16px;padding:11px 0;border-bottom:1px solid var(--line-2)}
.dgc .drow dt{color:var(--muted);font-size:13px}
.dgc .drow dd{margin:0;font-family:var(--mono);font-size:13.5px;text-align:right;font-weight:500}
.dgc .dsec{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);font-weight:700;
  margin:22px 0 4px}
.dgc .errbox{background:var(--fail-soft);border:1px solid #F2CFC9;border-radius:var(--r-m);padding:12px 14px;margin-top:10px}
.dgc .errbox .ec{font-family:var(--mono);font-size:12px;color:var(--fail);font-weight:600}
.dgc .errbox .em{font-size:13.5px;color:#7A1F14;margin-top:4px}
.dgc .dfoot{padding:14px 20px;border-top:1px solid var(--line);display:flex;gap:9px}
.dgc .btn{border-radius:var(--r-m);padding:11px 15px;font-weight:600;font-size:14px;display:flex;align-items:center;
  justify-content:center;gap:8px;transition:.15s;flex:1}
.dgc .btn-primary{background:var(--blue);color:#fff}
.dgc .btn-primary:hover{background:#0A57BC}
.dgc .btn-quiet{background:var(--offline-soft);color:var(--ink-2)}
.dgc .btn-quiet:hover{background:#E3E8F0}

.dgc .state{padding:64px 24px;text-align:center;color:var(--muted)}
.dgc .state .st{font-family:var(--display);font-weight:700;font-size:19px;color:var(--ink);margin-bottom:6px}
.dgc .state .btn{display:inline-flex;flex:none;margin-top:16px;padding:11px 20px}
.dgc .skel{background:linear-gradient(90deg,#EEF1F6 25%,#F7F9FC 37%,#EEF1F6 63%);background-size:400% 100%;
  animation:dgcSk 1.3s ease infinite;border-radius:6px}
@keyframes dgcSk{0%{background-position:100% 0}100%{background-position:0 0}}
.dgc .toast{position:fixed;left:50%;bottom:26px;transform:translate(-50%,20px);background:var(--navy);color:#fff;
  padding:12px 18px;border-radius:99px;font-weight:600;font-size:14px;z-index:1500;opacity:0;pointer-events:none;
  transition:.24s;box-shadow:var(--shadow-lg);display:flex;align-items:center;gap:9px}
.dgc .toast.show{opacity:1;transform:translate(-50%,0)}
.dgc .foot{margin-top:22px;display:flex;gap:16px;flex-wrap:wrap;align-items:center;color:var(--muted);font-size:12.5px}
.dgc .foot a{color:var(--blue);text-decoration:none;font-weight:600}
.dgc .foot a:hover{text-decoration:underline}

/* Live Bell Feed --------------------------------------------------------- */
.dgc .livefeed{margin-bottom:14px}
.dgc .lf-live{display:flex;align-items:center;gap:7px;font-size:12.5px;font-weight:700;color:var(--emerald);
  text-transform:uppercase;letter-spacing:.06em}
.dgc .lf-live .rec{width:9px;height:9px;border-radius:99px;background:var(--emerald);box-shadow:0 0 0 3px var(--emerald-soft)}
.dgc .lf-live.paused{color:var(--muted)}
.dgc .lf-live.paused .rec{background:var(--offline);box-shadow:0 0 0 3px var(--offline-soft)}
.dgc .fchip .fcount{font-family:var(--mono);font-size:11px;opacity:.85;margin-left:2px}
.dgc .fchip.lf-toggle{color:var(--ink-2)}
.dgc .sw.sw-played{background:var(--emerald)} .dgc .sw.sw-late{background:var(--gold)}
.dgc .sw.sw-failed{background:var(--fail)} .dgc .sw.sw-offline{background:var(--offline)}

.dgc .lf-list{max-height:392px;overflow-y:auto;overscroll-behavior:contain}
.dgc .lf-item{display:flex;align-items:center;gap:14px;padding:12px 18px;border-bottom:1px solid var(--line-2);
  transition:background .14s}
.dgc .lf-item:last-child{border-bottom:0}
.dgc .lf-item:hover{background:#FAFBFD}
.dgc .lf-badge{flex:none;width:42px;height:42px;border-radius:12px;display:flex;align-items:center;justify-content:center;
  font-size:19px;line-height:1;background:var(--offline-soft);color:var(--ink-2)}
.dgc .lf-badge.s-played{background:var(--emerald-soft);color:var(--emerald)}
.dgc .lf-badge.s-late{background:var(--gold-soft);color:#7A5600}
.dgc .lf-badge.s-failed{background:var(--fail-soft);color:var(--fail)}
.dgc .lf-badge.s-offline{background:var(--offline-soft);color:var(--offline)}
.dgc .lf-badge.s-pending{background:var(--blue-soft);color:#0A4FA8}
.dgc .lf-main{min-width:0;flex:1}
.dgc .lf-name{font-weight:600;font-size:14.5px;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.dgc .lf-sub{display:flex;align-items:center;gap:8px;margin-top:3px;font-size:12px;color:var(--muted);flex-wrap:wrap}
.dgc .lf-right{flex:none;text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:4px}
.dgc .lf-time{font-size:12px;color:var(--muted)}
.dgc .lf-pill{font-size:11px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;padding:3px 9px;border-radius:99px}
.dgc .lf-pill.p-played{background:var(--emerald-soft);color:#04603C}
.dgc .lf-pill.p-late{background:var(--gold-soft);color:#7A5600}
.dgc .lf-pill.p-failed{background:var(--fail-soft);color:var(--fail)}
.dgc .lf-pill.p-offline{background:var(--offline-soft);color:var(--offline)}
.dgc .lf-pill.p-pending{background:var(--blue-soft);color:#0A4FA8}
.dgc .lf-foot{display:flex;align-items:center;gap:12px;padding:11px 18px;border-top:1px solid var(--line-2);
  background:#FBFCFE;font-size:12px;color:var(--muted)}
.dgc .lf-foot .lf-hint{margin-left:auto}
.dgc .livefeed .state{padding:40px 24px}

.dgc[data-lang="ne"] .en{display:none}
.dgc[data-lang="en"] .ne{display:none}
.dgc[data-lang="ne"]{font-family:var(--body)}

@media (max-width:1180px){ .dgc .kpis{grid-template-columns:repeat(3,1fr)} .dgc .kpi.k-rate{grid-column:span 3} }
@media (max-width:820px){
  .dgc .rail{flex-wrap:wrap;padding:12px 14px;gap:10px}
  .dgc .rail-tools{margin-left:0;width:100%;flex-wrap:wrap}
  .dgc .schoolpick{flex:1 1 100%}
  .dgc .picker{min-width:0;max-width:none}
  .dgc .weeknav{flex:1}.dgc .weeknav .wlabel{flex:1;justify-content:center}
  .dgc .rail-sep{display:none}
  .dgc .kpis{grid-template-columns:repeat(2,1fr)} .dgc .kpi.k-rate{grid-column:span 2}
  .dgc .ctx-meta{margin-left:0;gap:18px}
  .dgc .wrap{padding:14px 12px 48px}
  .dgc .bellgrid{grid-template-columns:150px repeat(7,minmax(108px,1fr))}
}
@media (prefers-reduced-motion:reduce){.dgc *{animation:none !important;transition:none !important}}
@media print{.dgc .rail-tools,.dgc .filters,.dgc .banner{display:none}.dgc .card,.dgc .kpi{box-shadow:none}}
`;

// Injects the console CSS once and ensures the brand fonts are loaded.
function ConsoleStyles() {
    useEffect(() => {
        if (!document.getElementById("dgc-fonts")) {
            const link = document.createElement("link");
            link.id = "dgc-fonts";
            link.rel = "stylesheet";
            link.href = FONTS_HREF;
            document.head.appendChild(link);
        }
    }, []);
    return <style dangerouslySetInnerHTML={{__html: CONSOLE_CSS}} />;
}

export default ConsoleStyles;
